/**
 * DeleteEntryConfirmDialog.js
 * Dialog de confirmação para exclusão de lançamento contábil
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Receipt as JournalIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../helpers/formatCurrency';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { isReconciled } from '../../helpers/reconciliationHelpers';

const DeleteEntryConfirmDialog = ({ open, onClose, onConfirm, entry, isLoading = false }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  if (!entry) {
    return null;
  }

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return 'N/A';
    }
  };

  const isEntryReconciled = isReconciled(entry);

  return (
    <Dialog
      open={open}
      onClose={!isProcessing ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              backgroundColor: 'error.light',
              color: 'error.main',
            }}
          >
            <WarningIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Confirmar Exclusão
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta ação não pode ser desfeita
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* Informações do Lançamento */}
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <JournalIcon fontSize="small" />
              Lançamento Contábil
            </Typography>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                    {entry.description}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(entry.dateVencimento || entry.dateCompetencia)}
                    </Typography>
                    {entry.categoryName && (
                      <Chip
                        label={entry.categoryName}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Stack>
                </Box>
                <Chip
                  label={formatCurrency(Math.abs(entry.value || 0))}
                  color={entry.isInput ? 'success' : 'error'}
                  sx={{ fontWeight: 'bold' }}
                />
              </Stack>
            </Box>
          </Box>

          {/* Avisos */}
          {isEntryReconciled ? (
            <Alert severity="error">
              <Typography variant="body2">
                <strong>Erro:</strong> Não é possível excluir um lançamento que já foi conciliado.
                Primeiro, remova a conciliação deste lançamento.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Atenção:</strong> Esta ação excluirá permanentemente o lançamento contábil e
                não poderá ser desfeita. O lançamento será removido de todos os relatórios e
                registros do sistema.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={isProcessing} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isProcessing || isEntryReconciled}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ minWidth: 120 }}
        >
          {isProcessing ? 'Excluindo...' : 'Excluir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteEntryConfirmDialog;
